/**
 * Task deadline helpers — bridge between the UI's "date + optional time" form
 * and the DB's two columns:
 *
 *   - `due_date`     (date)        — date-only deadline ("by this day")
 *   - `deadline_at`  (timestamptz) — deadline with a specific time ("by 14:00 on this day")
 *
 * The two are mutually exclusive: when a time is given we store `deadline_at`
 * and clear `due_date`; otherwise we store `due_date` and clear `deadline_at`.
 * `deadline_at` always wins when both somehow exist (legacy rows).
 *
 * All times are interpreted in JST (Asia/Tokyo), matching the rest of the app.
 */

const JST = 'Asia/Tokyo'

interface DeadlineSource {
  due_date?: string | null
  deadline_at?: string | null
}

interface DeadlineFields {
  due_date: string | null
  deadline_at: string | null
}

/**
 * Split a task's deadline into the `{ date, time }` pair the form inputs use.
 * `date` is `YYYY-MM-DD` (or `''` when there is no deadline).
 * `time` is `HH:MM` in JST (or `''` when the deadline has no time component).
 */
export function splitDeadline(task: DeadlineSource): { date: string; time: string } {
  if (task.deadline_at) {
    const d = new Date(task.deadline_at)
    if (!isNaN(d.getTime())) {
      const date = d.toLocaleDateString('en-CA', { timeZone: JST }) // YYYY-MM-DD
      const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: JST }) // HH:MM
      return { date, time }
    }
  }
  return { date: task.due_date ? task.due_date.slice(0, 10) : '', time: '' }
}

/**
 * Build the `{ due_date, deadline_at }` columns to write for a given form state.
 * Pass the `date` (`YYYY-MM-DD`) and `time` (`HH:MM`, may be empty) from the inputs.
 */
export function buildDeadlineFields(date: string, time: string): DeadlineFields {
  if (!date) return { due_date: null, deadline_at: null }
  if (!time) return { due_date: date, deadline_at: null }
  const hhmmss = time.length === 5 ? `${time}:00` : time
  return { due_date: null, deadline_at: `${date}T${hhmmss}+09:00` }
}

/**
 * Format a task's deadline as a short badge label + colour, e.g.
 * `今日 14:00`, `明日`, `5/15 09:30`, `3日超過`. Returns `null` when no deadline.
 */
export function formatDeadline(
  task: DeadlineSource,
  todayStr: string,
): { label: string; color: string; bg: string; border: string } | null {
  const { date, time } = splitDeadline(task)
  if (!date) return null
  const timeSuffix = time ? ` ${time}` : ''

  if (date < todayStr) {
    const days = Math.floor((new Date(todayStr).getTime() - new Date(date).getTime()) / 86400000)
    return { label: `${days}日超過`, color: 'var(--red)', bg: 'var(--red-bg)', border: 'var(--red-border)' }
  }
  if (date === todayStr) {
    return { label: `今日${timeSuffix}`, color: 'var(--red)', bg: 'var(--red-bg)', border: 'var(--red-border)' }
  }
  const tomorrow = new Date(todayStr)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`
  if (date === tomorrowStr) {
    return { label: `明日${timeSuffix}`, color: 'var(--amber)', bg: 'var(--amber-bg)', border: 'var(--amber-border)' }
  }
  const [, m, d] = date.split('-')
  return { label: `${parseInt(m)}/${parseInt(d)}${timeSuffix}`, color: 'var(--text3)', bg: 'var(--surface2)', border: 'var(--border)' }
}
